/**
 * In-store mode (E10): the React wiring around lib/grocery-outbox.ts --
 * see that module for the semantics (last-write-wins queue, read-side
 * overlay, retryable-vs-verdict failure split). This hook owns the
 * browser-y parts the pure module deliberately doesn't: localStorage
 * mirroring, the `online` listener, the retry interval, and query-cache
 * invalidation after a queued check-off finally lands.
 *
 * Retry triggers: the `online` event (always -- a queued tap must sync as
 * soon as connectivity returns, whether or not Shopping mode is still on),
 * plus a polling interval while anything is queued (`online` is unreliable
 * on flaky-but-technically-connected store wifi), plus one attempt on
 * mount (a reload in the store must drain what the previous page queued).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as groceryListApi from "../api/grocery-lists";
import { parseCheckoffCausalTransportError } from "../api/grocery-lists";
import type { GroceryList } from "../api/types";
import { track } from "../analytics";
import {
  consumeRetryableDirectCheckoffError,
  isRetryableDirectCheckoffError,
  type RetryableDirectCheckoffError,
} from "../grocery-checkoff-retry";
import {
  compareCheckoffEpochs,
  confirmServerCheckoff,
  getConfirmedServerCheckoff,
  isCanonicalCheckoffEpoch,
} from "../grocery-checkoff-confirmed";
import {
  advanceGroceryCheckoffAnalytics,
  isGroceryList,
  snapshotGroceryCheckoffAnalytics,
  type GroceryCheckoffAnalyticsSnapshot,
} from "../grocery-checkoff-analytics";
import {
  CHECKOFF_OUTBOX_STORAGE_KEY,
  CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3,
  admitCheckoffOutboxEntry,
  applyPendingCheckoffs,
  compareCheckoffOperationFreshness,
  createCheckoffOperationId,
  enqueueCheckoff,
  flushOutbox,
  isCheckoffOperationNewerThan,
  isCurrentCheckoffOutboxEntry,
  loadAdmittedCheckoffRetirementV3Maxima,
  loadOutbox,
  mergeCheckoffRetirementV3StorageEvent,
  mergeGroceryCheckoffAnalyticsProgress,
  pendingCheckedState,
  parseCheckoffOperationId,
  parseCheckoffRetirementV3StorageRecord,
  persistOutboxUpdate,
  coordinateCheckoff,
  purgeConfirmedCheckoffTargetState,
  purgeDeletedCheckoffTargetState,
  retireCheckoffOperationsThrough,
  samePersistedCheckoffOperation,
  subscribeToCheckoffRetirements,
  type CheckoffOutboxEntry,
  type CheckoffOutboxEntryV2,
} from "../grocery-outbox";
import { GROCERY_LISTS_QUERY_KEY, MEAL_PLANS_QUERY_KEY } from "./query-keys";
import {
  invalidateCheckoffQueries,
  isAuthoritativeCausalToggleResult,
  reconcileAuthoritativeCausalState,
  reconcileAuthoritativeMutationItem,
} from "./use-grocery-lists";
import { resetCheckoffOperationProcessClock } from "../grocery-checkoff-operation";
import {
  CHECKOFF_POLICY_CEILING,
  savePolicyRecoveryMarker,
  type CheckoffPolicyRecoveryMarkerV1,
} from "../grocery-checkoff-recovery";

export { applyPendingCheckoffs };

/** Store wifi is flaky-but-connected more often than it is honestly offline,
 * so `online` alone can't be trusted to fire -- poll too, but gently. */
const RETRY_INTERVAL_MS = 15_000;
const RETIRED_CHECKOFF_ERROR = { status: 409 } as const;

function replayErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

// Exact direct failures are actionable in the realm that produced them even
// though their operation is necessarily above the last confirmed server
// boundary. Durable bytes alone never gain that provenance: a reload or a
// different realm must first obtain a baseline that admits them.
const locallyOwnedRetryEntries = new Set<string>();

function localRetryIdentity(entry: CheckoffOutboxEntry): string | undefined {
  return isCurrentCheckoffOutboxEntry(entry)
    ? JSON.stringify([
        entry.groceryListId,
        entry.itemId,
        entry.checked,
        entry.checkoffEpoch,
        entry.operationId,
        entry.queuedAt,
      ])
    : undefined;
}

function rememberLocallyOwnedRetry(entry: CheckoffOutboxEntryV2): void {
  locallyOwnedRetryEntries.add(localRetryIdentity(entry)!);
}

function forgetLocallyOwnedRetry(entry: CheckoffOutboxEntry): void {
  const identity = localRetryIdentity(entry);
  if (identity) locallyOwnedRetryEntries.delete(identity);
}

/** Test isolation for the hook's process-local direct-failure provenance. */
export function resetCheckoffOutboxHookStateForTests(): void {
  locallyOwnedRetryEntries.clear();
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Accessing localStorage itself can throw (privacy mode / blocked
    // site data) -- degrade to in-memory queueing.
    return null;
  }
}

function loadHookEntries(): CheckoffOutboxEntry[] {
  return loadOutbox(safeLocalStorage());
}

function retirementOperationIds(storage: Storage | null): string[] {
  return loadAdmittedCheckoffRetirementV3Maxima(
    storage,
    getConfirmedServerCheckoff,
  ).map(
    ({ supersededThroughOperationId }) => supersededThroughOperationId,
  );
}

function isLocallyActionableRetry(entry: CheckoffOutboxEntry): boolean {
  const identity = localRetryIdentity(entry);
  if (!identity || !locallyOwnedRetryEntries.has(identity)) return false;
  const admission = admitCheckoffOutboxEntry(
    entry,
    getConfirmedServerCheckoff(entry.groceryListId, entry.itemId),
  );
  return (
    admission.kind === "quarantine" &&
    admission.reason === "equal-epoch-above-boundary"
  );
}

function isReplayAdmitted(entry: CheckoffOutboxEntry): boolean {
  return (
    admitCheckoffOutboxEntry(
      entry,
      getConfirmedServerCheckoff(entry.groceryListId, entry.itemId),
    ).kind === "admit" || isLocallyActionableRetry(entry)
  );
}

function isReplayRetired(
  storage: Storage | null,
  entry: CheckoffOutboxEntry,
): boolean {
  if (!isCurrentCheckoffOutboxEntry(entry)) return false;
  return loadAdmittedCheckoffRetirementV3Maxima(
    storage,
    getConfirmedServerCheckoff,
  ).some((record) => {
    if (
      record.groceryListId !== entry.groceryListId ||
      record.itemId !== entry.itemId
    ) {
      return false;
    }
    const epochOrder = compareCheckoffEpochs(
      entry.checkoffEpoch,
      record.supersededThroughEpoch,
    );
    return (
      epochOrder < 0 ||
      (epochOrder === 0 &&
        compareCheckoffOperationFreshness(
          parseCheckoffOperationId(entry.operationId)!,
          parseCheckoffOperationId(record.supersededThroughOperationId)!,
        ) <= 0)
    );
  });
}

// A retry capability carries same-realm provenance from a direct request that
// was actually sent. Its exact operation may therefore be cancelled by a
// durable retirement newer than the baseline this tab has observed. Keep this
// check separate from replay admission: arbitrary durable queue bytes remain
// baseline-gated by isReplayRetired/actionableReplayEntries.
function isDirectRetryRetired(
  storage: Storage | null,
  entry: CheckoffOutboxEntry,
): boolean {
  const baseline = getConfirmedServerCheckoff(entry.groceryListId, entry.itemId);
  if (!storage || !baseline || !isCurrentCheckoffOutboxEntry(entry)) {
    return false;
  }
  const operation = parseCheckoffOperationId(entry.operationId);
  if (!operation) return false;
  try {
    const keyAt = storage.key;
    const getItem = storage.getItem;
    const length = storage.length;
    if (
      typeof keyAt !== "function" ||
      typeof getItem !== "function" ||
      !Number.isSafeInteger(length) ||
      length < 0
    ) {
      return false;
    }
    for (let index = 0; index < length; index += 1) {
      const key = keyAt.call(storage, index);
      if (!key?.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3)) continue;
      const record = parseCheckoffRetirementV3StorageRecord(
        key,
        getItem.call(storage, key),
      );
      if (
        !record ||
        record.groceryListId !== entry.groceryListId ||
        record.itemId !== entry.itemId ||
        // A durable retirement above the last authoritative epoch is only a
        // quarantine input. It must not cancel a direct retry until that
        // epoch is confirmed by the server.
        compareCheckoffEpochs(
          record.supersededThroughEpoch,
          baseline.checkoffEpoch,
        ) > 0
      ) {
        continue;
      }
      const epochOrder = compareCheckoffEpochs(
        entry.checkoffEpoch,
        record.supersededThroughEpoch,
      );
      if (
        epochOrder < 0 ||
        (epochOrder === 0 &&
          compareCheckoffOperationFreshness(
            operation,
            parseCheckoffOperationId(record.supersededThroughOperationId)!,
          ) <= 0)
      ) {
        return true;
      }
    }
  } catch {
    // Storage can disappear or become unreadable between the capability
    // check and enumeration. Preserve the genuine retry in that case.
    return false;
  }
  return false;
}

function shouldRetainReplayEntry(
  storage: Storage | null,
  entry: CheckoffOutboxEntry,
): boolean {
  const admission = admitCheckoffOutboxEntry(
    entry,
    getConfirmedServerCheckoff(entry.groceryListId, entry.itemId),
  );
  return (
    !isReplayRetired(storage, entry) &&
    admission.kind !== "drop" &&
    admission.kind !== "purge"
  );
}

function actionableReplayEntries(
  storage: Storage | null,
  entries: readonly CheckoffOutboxEntry[],
): CheckoffOutboxEntryV2[] {
  return entries.filter(
    (entry): entry is CheckoffOutboxEntryV2 =>
      isCurrentCheckoffOutboxEntry(entry) &&
      isReplayAdmitted(entry) &&
      !isReplayRetired(storage, entry),
  );
}

function publishSuccessfulReplayRetirement(
  storage: Storage | null,
  entry: CheckoffOutboxEntry,
  sharedCausalBoundary: string | undefined,
): void {
  if (
    typeof sharedCausalBoundary !== "string" ||
    !parseCheckoffOperationId(sharedCausalBoundary)
  )
    return;
  try {
    retireCheckoffOperationsThrough(
      storage,
      entry.groceryListId,
      entry.itemId,
      sharedCausalBoundary,
      entry.checkoffEpoch,
    );
  } catch {
    // An accepted server response remains accepted if browser storage fails.
    // The retirement module preserves same-realm knowledge when it can merge.
  }
}

function hasStrictlyNewerReplayRetirement(
  storage: Storage | null,
  entry: CheckoffOutboxEntry,
  sharedCausalBoundary: string | undefined,
): boolean {
  const successfulBoundary = parseCheckoffOperationId(sharedCausalBoundary);
  if (!successfulBoundary) {
    return isReplayRetired(storage, entry);
  }
  const maximum = loadAdmittedCheckoffRetirementV3Maxima(
    storage,
    getConfirmedServerCheckoff,
  )
    .filter(
      (record) =>
        record.groceryListId === entry.groceryListId &&
        record.itemId === entry.itemId &&
        record.supersededThroughEpoch === entry.checkoffEpoch,
    )
    .map((record) => parseCheckoffOperationId(record.supersededThroughOperationId)!)
    .reduce<ReturnType<typeof parseCheckoffOperationId>>(
      (current, candidate) =>
        !current || compareCheckoffOperationFreshness(candidate, current) > 0
          ? candidate
          : current,
      undefined,
    );
  return (
    maximum !== undefined &&
    compareCheckoffOperationFreshness(maximum, successfulBoundary) > 0
  );
}

export interface CheckoffOutbox {
  /** itemId -> chosen-but-unconfirmed checked state, for THIS list. */
  pendingChecked: Map<string, boolean>;
  /** Queue a check-off that failed to reach the server. */
  enqueue: (
    itemId: string,
    checked: boolean,
    retryFailure: RetryableDirectCheckoffError,
  ) => void;
  /** Reconcile storage and reserve the causal cutoff for a direct intent. */
  capturePendingOperationBoundary: (itemId: string) => string;
  /** Remove a queued value after a direct request confirms the item. */
  resolve: (
    itemId: string,
    checked: boolean,
    supersededThroughOperationId?: string,
    supersededThroughEpoch?: string,
  ) => void;
  /** Discard all queued values for this list after the list is cleared. */
  discardList: () => void;
  /** Attempt to drain the queue now (also runs automatically). */
  flush: () => Promise<void>;
}

export function useCheckoffOutbox(
  groceryListId: string | undefined,
): CheckoffOutbox {
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<CheckoffOutboxEntry[]>(() =>
    loadHookEntries(),
  );
  const entriesRef = useRef(entries);
  const flushingRef = useRef(false);

  const freshestCachedGroceryLists = useCallback((): GroceryList[] => {
    const freshestByListId = new Map<
      string,
      {
        list: GroceryList;
        dataUpdatedAt: number;
        observerCount: number;
        queryHash: string;
      }
    >();
    queryClient
      .getQueryCache()
      .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
      .forEach((query) => {
        const value = query.state.data;
        if (!isGroceryList(value)) return;
        const candidate = {
          list: value,
          dataUpdatedAt: query.state.dataUpdatedAt,
          observerCount: query.getObserversCount(),
          queryHash: query.queryHash,
        };
        const current = freshestByListId.get(value.id);
        if (
          !current ||
          candidate.dataUpdatedAt > current.dataUpdatedAt ||
          (candidate.dataUpdatedAt === current.dataUpdatedAt &&
            candidate.observerCount > current.observerCount) ||
          (candidate.dataUpdatedAt === current.dataUpdatedAt &&
            candidate.observerCount === current.observerCount &&
            candidate.queryHash > current.queryHash)
        ) {
          freshestByListId.set(value.id, candidate);
        }
      });
    return [...freshestByListId.values()].map(({ list }) => list);
  }, [queryClient]);

  const persist = useCallback(
    (
      next: CheckoffOutboxEntry[],
      analyticsProgressByListId?: ReadonlyMap<
        string,
        GroceryCheckoffAnalyticsSnapshot
      >,
      authoritativeMembershipListIds?: ReadonlySet<string>,
    ) => {
      const reconciled = persistOutboxUpdate(
        safeLocalStorage(),
        entriesRef.current,
        next,
        analyticsProgressByListId,
        authoritativeMembershipListIds,
        getConfirmedServerCheckoff,
      );
      entriesRef.current = reconciled;
      setEntries(reconciled);
    },
    [],
  );

  // localStorage does not fire a storage event in the tab that performed the
  // write. The module subscription closes that same-tab gap; the storage
  // listener below covers other tabs and reloads.
  useEffect(
    () =>
      subscribeToCheckoffRetirements(() => {
        const storage = safeLocalStorage();
        const retained = entriesRef.current.filter((entry) =>
          shouldRetainReplayEntry(storage, entry),
        );
        if (retained.length === entriesRef.current.length) return;
        entriesRef.current
          .filter(
            (entry) =>
              !retained.some((candidate) =>
                samePersistedCheckoffOperation(entry, candidate),
              ),
          )
          .forEach(forgetLocallyOwnedRetry);
        // Same-realm publication has no StorageEvent. Persist through the
        // normal merge path so a retired queue is physically removed before
        // a remount/reload can observe it again.
        persist(retained);
      }),
    [persist],
  );

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      const isRetirementRecord =
        event.key?.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3) ?? false;
      if (
        event.key !== null &&
        event.key !== CHECKOFF_OUTBOX_STORAGE_KEY &&
        !isRetirementRecord
      ) {
        return;
      }
      if (isRetirementRecord) {
        mergeCheckoffRetirementV3StorageEvent(event.key, event.newValue);
      }
      const storage = safeLocalStorage();
      const loaded = loadOutbox(storage);
      entriesRef.current
        .filter(
          (entry) =>
            !loaded.some((candidate) =>
              localRetryIdentity(candidate) === localRetryIdentity(entry),
            ),
        )
        .forEach(forgetLocallyOwnedRetry);
      entriesRef.current = loaded;
      setEntries(loaded);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const flush = useCallback(async () => {
    if (flushingRef.current || entriesRef.current.length === 0) return;
    const storage = safeLocalStorage();
    const retainedEntries = entriesRef.current.filter((entry) =>
      shouldRetainReplayEntry(storage, entry),
    );
    if (retainedEntries.length !== entriesRef.current.length) {
      entriesRef.current
        .filter(
          (entry) =>
            !retainedEntries.some((candidate) =>
              samePersistedCheckoffOperation(entry, candidate),
            ),
        )
        .forEach(forgetLocallyOwnedRetry);
      persist(retainedEntries);
    }
    const eligibleEntries = actionableReplayEntries(storage, retainedEntries);
    if (eligibleEntries.length === 0) return;
    flushingRef.current = true;
    try {
      const snapshot = eligibleEntries;
      const analyticsByListId = new Map<
        string,
        GroceryCheckoffAnalyticsSnapshot
      >();
      const authoritativeMembershipListIds = new Set<string>();
      freshestCachedGroceryLists().forEach((value) => {
        const authoritativeSnapshot = snapshotGroceryCheckoffAnalytics(value);
        analyticsByListId.set(value.id, authoritativeSnapshot);
        authoritativeMembershipListIds.add(value.id);
      });
      snapshot.forEach((entry) => {
        if (entry.analyticsSnapshot) {
          analyticsByListId.set(
            entry.groceryListId,
            mergeGroceryCheckoffAnalyticsProgress(
              entry.analyticsSnapshot,
              analyticsByListId.get(entry.groceryListId),
              {
                authoritativeMembership: authoritativeMembershipListIds.has(
                  entry.groceryListId,
                ),
              },
            ) ?? entry.analyticsSnapshot,
          );
        }
      });
      const { remaining, sentCount } = await flushOutbox(
        snapshot,
        async (entry) => {
          if (!isCurrentCheckoffOutboxEntry(entry)) {
            throw RETIRED_CHECKOFF_ERROR;
          }
          const currentEntry = entry;
          if (isReplayRetired(storage, currentEntry)) {
            throw RETIRED_CHECKOFF_ERROR;
          }
          const operation = coordinateCheckoff(
            currentEntry.groceryListId,
            currentEntry.itemId,
            currentEntry.checked,
            () => {
              // A coordinated replay can wait behind an opposite target.
              // Re-check its exact persisted identity at the actual network
              // boundary. A newer same-target direct owner must not turn this
              // retired uncertain request into a different wire operation.
              if (isReplayRetired(storage, currentEntry)) {
                throw RETIRED_CHECKOFF_ERROR;
              }
              // A persisted retry reuses the exact uncertain direct request.
              // Joined boundaries lift only the post-success retirement; they
              // must never rewrite the operation placed on wire.
              const wireOperationId = currentEntry.operationId;
              return Promise.resolve(
                groceryListApi.toggleGroceryItem(
                  currentEntry.groceryListId,
                  currentEntry.itemId,
                  currentEntry.checked,
                  {
                    checkoffEpoch: currentEntry.checkoffEpoch,
                    operationId: wireOperationId,
                  },
                ),
              )
                .catch((error: unknown) => {
                  const status = replayErrorStatus(error);
                  if (status === 404) {
                    entriesRef.current
                      .filter(
                        (entry) =>
                          entry.groceryListId === currentEntry.groceryListId &&
                          entry.itemId === currentEntry.itemId,
                      )
                      .forEach(forgetLocallyOwnedRetry);
                    purgeDeletedCheckoffTargetState(
                      storage,
                      currentEntry.groceryListId,
                      currentEntry.itemId,
                    );
                    invalidateCheckoffQueries(queryClient);
                    throw error;
                  }

                  const causalError = parseCheckoffCausalTransportError(error);
                  if (causalError?.kind === "policy-rejected") {
                    const details = causalError.details;
                    if (
                      details.oldEpoch === currentEntry.checkoffEpoch &&
                      details.rejectedOperationId === currentEntry.operationId
                    ) {
                      purgeConfirmedCheckoffTargetState(
                        storage,
                        currentEntry.groceryListId,
                        currentEntry.itemId,
                        details.newEpoch,
                      );
                      confirmServerCheckoff(
                        currentEntry.groceryListId,
                        currentEntry.itemId,
                        {
                          checked: details.checked,
                          checkoffEpoch: details.newEpoch,
                          checkoffOperationId: null,
                        },
                        details.serverTimeMs,
                      );
                      const marker: CheckoffPolicyRecoveryMarkerV1 = {
                        schemaVersion: 1,
                        itemId: currentEntry.itemId,
                        serverTimeMs: details.serverTimeMs,
                        oldEpoch: details.oldEpoch,
                        newEpoch: details.newEpoch,
                        rejectedOperationId: details.rejectedOperationId,
                        policyCeiling: CHECKOFF_POLICY_CEILING,
                      };
                      resetCheckoffOperationProcessClock();
                      savePolicyRecoveryMarker(storage, marker);
                      reconcileAuthoritativeCausalState(
                        queryClient,
                        currentEntry.groceryListId,
                        currentEntry.itemId,
                        details.checked,
                        details.newEpoch,
                        null,
                        details.serverTimeMs,
                      );
                    }
                    invalidateCheckoffQueries(queryClient);
                    throw error;
                  }
                  if (causalError?.kind === "required") {
                    reconcileAuthoritativeCausalState(
                      queryClient,
                      currentEntry.groceryListId,
                      currentEntry.itemId,
                      causalError.details.checked,
                      causalError.details.acceptedEpoch,
                      causalError.details.acceptedOperationId,
                    );
                    invalidateCheckoffQueries(queryClient);
                    throw error;
                  }
                  if (status === 409 || status === 428) {
                    invalidateCheckoffQueries(queryClient);
                  }
                  throw error;
                });
            },
            currentEntry.operationId,
            currentEntry.checkoffEpoch,
            currentEntry.operationId,
          );
          if (operation.role === "stale") throw RETIRED_CHECKOFF_ERROR;
          const result = await operation.promise;
          if (!operation.wasDispatched() || !result) {
            throw RETIRED_CHECKOFF_ERROR;
          }
          if (
            !isAuthoritativeCausalToggleResult(result, {
              groceryListId: currentEntry.groceryListId,
              itemId: currentEntry.itemId,
              checked: currentEntry.checked,
              checkoffEpoch: currentEntry.checkoffEpoch,
              operationId: currentEntry.operationId,
            })
          ) {
            throw new Error("Invalid causal check-off replay response");
          }
          const latestBaseline = getConfirmedServerCheckoff(
            currentEntry.groceryListId,
            currentEntry.itemId,
          );
          if (
            latestBaseline &&
            compareCheckoffEpochs(
              latestBaseline.checkoffEpoch,
              currentEntry.checkoffEpoch,
            ) > 0
          ) {
            throw RETIRED_CHECKOFF_ERROR;
          }
          // Coordinator cleanup is registered before any consumer
          // continuation, so membership is closed and this shared maximum is
          // frozen now. Publish it before analytics, outbox cleanup, cache
          // reconciliation, or any other successful-settlement side effect.
          const sharedCausalBoundary = operation.sharedCausalBoundary();
          publishSuccessfulReplayRetirement(
            storage,
            currentEntry,
            sharedCausalBoundary,
          );

          // A request already on the wire cannot be unsent, but a retirement
          // that lands while it is pending must suppress stale telemetry and
          // prevent the retry callback from restoring the operation. Equality
          // is this request's own publication; only a strictly newer boundary
          // suppresses its owner.
          if (
            hasStrictlyNewerReplayRetirement(
              storage,
              currentEntry,
              sharedCausalBoundary,
            )
          ) {
            throw RETIRED_CHECKOFF_ERROR;
          }

          reconcileAuthoritativeMutationItem(
            queryClient,
            currentEntry.groceryListId,
            result,
            result.causal.serverTimeMs,
          );

          if (
            operation.role === "owner" &&
            !operation.isSuperseded() &&
            result.causal.outcome === "applied"
          ) {
            track("grocery_item_toggled", { purchased: result.checked });
            const { next, completion } = advanceGroceryCheckoffAnalytics(
              analyticsByListId.get(currentEntry.groceryListId),
              currentEntry.itemId,
              result.checked,
            );
            if (completion) {
              track("grocery_list_completed", {
                itemCount: completion.itemCount,
                mealPlanId: completion.mealPlanId,
              });
            }
            if (next) analyticsByListId.set(currentEntry.groceryListId, next);
          }

          return result;
        },
      );
      const snapshotByItemId = new Map(
        snapshot.map((entry) => [entry.itemId, entry]),
      );
      snapshot
        .filter(
          (entry) =>
            !remaining.some((candidate) =>
              samePersistedCheckoffOperation(entry, candidate),
            ),
        )
        .forEach(forgetLocallyOwnedRetry);
      persist(
        entriesRef.current.flatMap((entry) => {
          const snapshotEntry = snapshotByItemId.get(entry.itemId);
          const carryAnalyticsProgress = () => {
            const analyticsSnapshot = analyticsByListId.get(
              entry.groceryListId,
            );
            return analyticsSnapshot ? { ...entry, analyticsSnapshot } : entry;
          };
          if (
            snapshotEntry === undefined ||
            !samePersistedCheckoffOperation(entry, snapshotEntry)
          ) {
            return [carryAnalyticsProgress()];
          }
          if (
            !remaining.some((candidate) =>
              samePersistedCheckoffOperation(entry, candidate),
            )
          ) {
            return [];
          }
          return [carryAnalyticsProgress()];
        }),
        analyticsByListId,
        authoritativeMembershipListIds,
      );
      if (sentCount > 0) {
        // The server finally has the taps -- refetch so the cache's checked
        // state matches before the overlay entries disappear.
        void queryClient.invalidateQueries({
          queryKey: [GROCERY_LISTS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [MEAL_PLANS_QUERY_KEY],
        });
      }
    } finally {
      flushingRef.current = false;
    }
  }, [freshestCachedGroceryLists, persist, queryClient]);

  const enqueue = useCallback(
    (
      itemId: string,
      checked: boolean,
      retryFailure: RetryableDirectCheckoffError,
    ) => {
      if (!groceryListId) return;
      const retryIdentity = consumeRetryableDirectCheckoffError(retryFailure, {
        groceryListId,
        itemId,
        checked,
      });
      if (
        !isRetryableDirectCheckoffError(retryFailure) ||
        !retryIdentity ||
        !isCanonicalCheckoffEpoch(retryIdentity.checkoffEpoch) ||
        parseCheckoffOperationId(retryIdentity.operationId)?.version !== 3
      ) {
        return;
      }
      const currentList = freshestCachedGroceryLists().find(
        (value) => value.id === groceryListId,
      );
      const entry: CheckoffOutboxEntryV2 = {
        schemaVersion: 2,
        operationId: retryIdentity.operationId,
        groceryListId,
        itemId,
        checked,
        checkoffEpoch: retryIdentity.checkoffEpoch,
        queuedAt: retryIdentity.queuedAt,
        ...(currentList
          ? {
              analyticsSnapshot:
                snapshotGroceryCheckoffAnalytics(currentList),
            }
          : {}),
      };
      // A direct request may have failed in this tab after another tab
      // durably retired the same causal operation. Consult the durable
      // retirement boundary before admission so a stale callback cannot
      // resurrect work that the other tab already superseded.
      if (isDirectRetryRetired(safeLocalStorage(), entry)) return;
      const admission = admitCheckoffOutboxEntry(
        entry,
        getConfirmedServerCheckoff(groceryListId, itemId),
      );
      if (
        admission.kind !== "admit" &&
        !(
          admission.kind === "quarantine" &&
          admission.reason === "equal-epoch-above-boundary"
        )
      ) {
        return;
      }
      // Reconcile sibling-hook storage before last-write-wins. One direct
      // failure capability fixes one queuedAt, so an exact match is already
      // the same retry rather than a second quarantine candidate.
      persist(entriesRef.current);
      if (
        entriesRef.current.some(
          (candidate) =>
            localRetryIdentity(candidate) === localRetryIdentity(entry),
        )
      ) {
        rememberLocallyOwnedRetry(entry);
        return;
      }
      entriesRef.current
        .filter(
          (candidate) =>
            candidate.groceryListId === groceryListId &&
            candidate.itemId === itemId &&
            localRetryIdentity(candidate) !== localRetryIdentity(entry),
        )
        .forEach(forgetLocallyOwnedRetry);
      rememberLocallyOwnedRetry(entry);
      persist(
        enqueueCheckoff(entriesRef.current, entry),
      );
    },
    [freshestCachedGroceryLists, groceryListId, persist],
  );

  const capturePendingOperationBoundary = useCallback(
    (itemId: string) => {
      const storage = safeLocalStorage();
      // Pull in writes made by other mounted instances before reserving the
      // direct request's causal cutoff. Persist is synchronous, so the overlay
      // reflects that reconciliation before this function returns.
      persist(entriesRef.current);
      return createCheckoffOperationId(
        storage,
        actionableReplayEntries(storage, entriesRef.current).filter(
          (entry) =>
            entry.groceryListId === groceryListId && entry.itemId === itemId,
        ),
        retirementOperationIds(storage),
      );
    },
    [groceryListId, persist],
  );

  const resolve = useCallback(
    (
      itemId: string,
      checked: boolean,
      supersededThroughOperationId?: string,
      supersededThroughEpoch?: string,
    ) => {
      // A different tab can persist between capture and success. Reconcile it
      // now, then retire everything causally at/before the direct request while
      // retaining operations created after that cutoff.
      if (supersededThroughOperationId) persist(entriesRef.current);
      if (
        groceryListId &&
        supersededThroughOperationId &&
        isCanonicalCheckoffEpoch(supersededThroughEpoch)
      ) {
        retireCheckoffOperationsThrough(
          safeLocalStorage(),
          groceryListId,
          itemId,
          supersededThroughOperationId,
          supersededThroughEpoch,
        );
      }
      const previousEntries = entriesRef.current;
      const retainedEntries = previousEntries.filter(
        (entry) =>
          entry.groceryListId !== groceryListId ||
          entry.itemId !== itemId ||
          (supersededThroughOperationId
            ? isCheckoffOperationNewerThan(
                entry,
                supersededThroughOperationId,
              )
            : entry.checked !== checked),
      );
      previousEntries
        .filter(
          (entry) =>
            !retainedEntries.some((candidate) =>
              samePersistedCheckoffOperation(entry, candidate),
            ),
        )
        .forEach(forgetLocallyOwnedRetry);
      persist(retainedEntries);
    },
    [groceryListId, persist],
  );

  const discardList = useCallback(() => {
    if (!groceryListId) return;
    // Reconcile first: another same-realm hook can persist work without
    // updating this hook's ref (localStorage emits no same-tab StorageEvent).
    // Exact retirements then serve as tombstones for every mounted observer,
    // preventing a sibling's stale ref from merging the discarded work back.
    persist(entriesRef.current);
    const discardedEntries = entriesRef.current.filter(
      (entry): entry is CheckoffOutboxEntryV2 =>
        entry.groceryListId === groceryListId &&
        isCurrentCheckoffOutboxEntry(entry),
    );
    discardedEntries.forEach((entry) => {
      retireCheckoffOperationsThrough(
        safeLocalStorage(),
        entry.groceryListId,
        entry.itemId,
        entry.operationId,
        entry.checkoffEpoch,
      );
    });
    discardedEntries.forEach(forgetLocallyOwnedRetry);
    persist(
      entriesRef.current.filter(
        (entry) => entry.groceryListId !== groceryListId,
      ),
    );
  }, [groceryListId, persist]);

  // Drain on reconnect, and once on mount (a reload mid-trip must sync
  // whatever the previous page load queued).
  useEffect(() => {
    const onOnline = () => void flush();
    window.addEventListener("online", onOnline);
    void flush();
    return () => window.removeEventListener("online", onOnline);
  }, [flush]);

  // Gentle retry interval, only while something is actually queued.
  const hasEntries =
    actionableReplayEntries(safeLocalStorage(), entries).length > 0;
  useEffect(() => {
    if (!hasEntries) return;
    const id = window.setInterval(() => void flush(), RETRY_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [hasEntries, flush]);

  const pendingChecked = pendingCheckedState(
    actionableReplayEntries(safeLocalStorage(), entries),
    groceryListId,
  );

  return {
    pendingChecked,
    enqueue,
    capturePendingOperationBoundary,
    resolve,
    discardList,
    flush,
  };
}
